import { Users } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { useTranslation } from "react-i18next";
import { XStack, YStack } from "tamagui";
import type { UserReaction } from "../../../activities/ActivityModel";
import { REACTIONS, type Reaction } from "../../../activities/types";
import { useStores } from "../../../core/stores";
import { Block } from "../../../ui/Block";
import { Body } from "../../../ui/Body";
import { Group } from "../../../ui/Group";
import type { InfoRowItemProps } from "../../../ui/InfoRow";
import { Sheet, type SheetProps } from "../../../ui/Sheet";
import { Title } from "../../../ui/Title";

export const FeedItemReactionShareSheet: React.FC<
  SheetProps & {
    feedItemUid?: string;
    commentItemUid?: string;
    commentReplyItemUid?: string;
  }
> = observer(
  ({ feedItemUid, commentItemUid, commentReplyItemUid, open, ...props }) => {
    const { t } = useTranslation();
    const {
      usersStore,
      businessFeedItemsStore,
      businessFeedCommentsStore,
      businessFeedCommentsReplyStore,
    } = useStores();

    const feedItem =
      feedItemUid && businessFeedItemsStore.byId.get(feedItemUid);
    const commentItem =
      commentItemUid && businessFeedCommentsStore.byId.get(commentItemUid);
    const commentReplyItem =
      commentReplyItemUid &&
      businessFeedCommentsReplyStore.byId.get(commentReplyItemUid);

    const toggleReaction = React.useCallback(
      (r: Reaction) => {
        if (feedItem) {
          feedItem.toggleReaction(r);
          props.onOpenChange?.(false);
        }
        if (commentItem) {
          commentItem.toggleReaction(r);
          props.onOpenChange?.(false);
        }
        if (commentReplyItem) {
          commentReplyItem.toggleReaction(r);
          props.onOpenChange?.(false);
        }
      },
      [feedItem, commentItem, commentReplyItem, props.onOpenChange],
    );

    if (!open || (!feedItem && !commentItem && !commentReplyItem)) {
      return null;
    }

    return (
      <Sheet open={open} {...props} sheetId="b-reactions">
        <YStack gap="$space.large">
          <Title>{t("business:feed_reactions")}</Title>

          <Group>
            {REACTIONS.map((r) => {
              const Icon = r.icon;

              let storedReactions: UserReaction[] = [];
              if (feedItem) {
                storedReactions = feedItem.data.reactions;
              } else if (commentItem) {
                storedReactions = commentItem.data.reactions;
              } else if (commentReplyItem) {
                storedReactions = commentReplyItem.data.reactions;
              }

              const userListReacted = storedReactions
                .filter((reaction) => reaction.reaction === r.type)
                .map((rr) => usersStore.byId.get(rr.userUid)?.data.displayName);

              const infoRowItems: InfoRowItemProps[] = userListReacted.length
                ? [{ icon: Users, text: userListReacted.join(", ") }]
                : [];

              return (
                <Block
                  key={r.type}
                  grouped
                  infoRowItems={infoRowItems}
                  onPress={() => toggleReaction(r.type)}
                >
                  <XStack alignItems="center" gap="$medium">
                    <Body>
                      <Icon />
                    </Body>
                    <Body>{r.title}</Body>
                  </XStack>
                </Block>
              );
            })}
          </Group>
        </YStack>
      </Sheet>
    );
  },
);
